import { Dream } from '../types/dreamSimulator';
import { GraduationCap, Home, Briefcase, Heart, PiggyBank, Building2, Plane, Baby, Stethoscope, Landmark, Leaf, Palmtree } from 'lucide-react';

export const predefinedDreams: Dream[] = [
  {
    id: 'retirement',
    name: 'Retiro Digno',
    description: 'Asegura un retiro cómodo y sin preocupaciones',
    category: 'retirement',
    icon: PiggyBank,
    difficulty: 'medium',
    timeline: 'long',
    monthlyGoal: 80000,
    timeframe: 20,
    certificatesNeeded: 8,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 20,
        waterSaved: 50000,
        landPreserved: 8000
      },
      socialImpact: {
        jobsCreated: 4,
        familiesSupported: 8,
        communityProjects: 1.2
      },
      economicImpact: {
        localEconomyBoost: 0.8,
        taxContribution: 120000,
        sustainableGrowth: 1.5
      }
    },
    details: [
      'Ingreso mensual para mantener tu estilo de vida',
      'Cobertura de gastos médicos y emergencias',
      'Fondo para viajes y actividades recreativas',
      'Patrimonio heredable para la siguiente generación'
    ]
  },
  {
    id: 'early-retirement',
    name: 'Retiro Anticipado',
    description: 'Libertad financiera antes de los 50 años',
    category: 'retirement',
    icon: Landmark,
    difficulty: 'hard',
    timeline: 'medium',
    monthlyGoal: 120000,
    timeframe: 15,
    certificatesNeeded: 15,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 37.5,
        waterSaved: 93750,
        landPreserved: 15000
      },
      socialImpact: {
        jobsCreated: 7.5,
        familiesSupported: 15,
        communityProjects: 2.25
      },
      economicImpact: {
        localEconomyBoost: 1.5,
        taxContribution: 225000,
        sustainableGrowth: 2.8
      }
    },
    details: [
      'Independencia financiera completa',
      'Libertad para dedicarte a tus pasiones',
      'Ingresos pasivos superiores a tus gastos',
      'Capacidad para reinvertir y seguir creciendo'
    ]
  },
  {
    id: 'education',
    name: 'Educación Universitaria',
    description: 'Asegura la educación superior de tus hijos',
    category: 'education',
    icon: GraduationCap,
    difficulty: 'medium',
    timeline: 'medium',
    monthlyGoal: 50000,
    timeframe: 15,
    certificatesNeeded: 5,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 12.5,
        waterSaved: 31250,
        landPreserved: 5000
      },
      socialImpact: {
        jobsCreated: 2.5,
        familiesSupported: 5,
        communityProjects: 0.75
      },
      economicImpact: {
        localEconomyBoost: 0.5,
        taxContribution: 75000,
        sustainableGrowth: 0.9
      }
    },
    details: [
      'Cubre costos de matrícula y gastos universitarios',
      'Incluye posibilidad de estudios en el extranjero',
      'Fondo para maestría o especialización',
      'Reinversión de utilidades para maximizar rendimientos'
    ]
  },
  {
    id: 'international-education',
    name: 'Educación Internacional',
    description: 'Estudios en las mejores universidades del mundo',
    category: 'education',
    icon: GraduationCap,
    difficulty: 'hard',
    timeline: 'medium',
    monthlyGoal: 100000,
    timeframe: 15,
    certificatesNeeded: 10,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 25,
        waterSaved: 62500,
        landPreserved: 10000
      },
      socialImpact: {
        jobsCreated: 5,
        familiesSupported: 10,
        communityProjects: 1.5
      },
      economicImpact: {
        localEconomyBoost: 1,
        taxContribution: 150000,
        sustainableGrowth: 1.8
      }
    },
    details: [
      'Estudios completos en universidades de prestigio internacional',
      'Cobertura de matrícula, alojamiento y manutención',
      'Posibilidad de múltiples títulos y especializaciones',
      'Fondo para networking y oportunidades globales'
    ]
  },
  {
    id: 'dream-home',
    name: 'Casa de Ensueño',
    description: 'Adquiere la casa que siempre has soñado',
    category: 'home',
    icon: Home,
    difficulty: 'hard',
    timeline: 'medium',
    monthlyGoal: 70000,
    timeframe: 10,
    certificatesNeeded: 7,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 17.5,
        waterSaved: 43750,
        landPreserved: 7000
      },
      socialImpact: {
        jobsCreated: 3.5,
        familiesSupported: 7,
        communityProjects: 1.05
      },
      economicImpact: {
        localEconomyBoost: 0.7,
        taxContribution: 105000,
        sustainableGrowth: 1.3
      }
    },
    details: [
      'Enganche para casa en zona residencial premium',
      'Fondo para remodelaciones y personalización',
      'Cobertura de gastos notariales y trámites',
      'Protección contra incrementos en el mercado inmobiliario'
    ]
  },
  {
    id: 'vacation-home',
    name: 'Casa de Vacaciones',
    description: 'Un segundo hogar para disfrutar y descansar',
    category: 'home',
    icon: Palmtree,
    difficulty: 'medium',
    timeline: 'medium',
    monthlyGoal: 40000,
    timeframe: 12,
    certificatesNeeded: 4,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 10,
        waterSaved: 25000,
        landPreserved: 4000
      },
      socialImpact: {
        jobsCreated: 2,
        familiesSupported: 4,
        communityProjects: 0.6
      },
      economicImpact: {
        localEconomyBoost: 0.4,
        taxContribution: 60000,
        sustainableGrowth: 0.7
      }
    },
    details: [
      'Propiedad en destino turístico o playa',
      'Posibilidad de generar ingresos por renta vacacional',
      'Fondo para mantenimiento y mejoras',
      'Inversión que combina disfrute y apreciación'
    ]
  },
  {
    id: 'business',
    name: 'Negocio Propio',
    description: 'Emprende tu propio negocio',
    category: 'business',
    icon: Briefcase,
    difficulty: 'medium',
    timeline: 'short',
    monthlyGoal: 60000,
    timeframe: 8,
    certificatesNeeded: 6,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 15,
        waterSaved: 37500,
        landPreserved: 6000
      },
      socialImpact: {
        jobsCreated: 3,
        familiesSupported: 6,
        communityProjects: 0.9
      },
      economicImpact: {
        localEconomyBoost: 0.6,
        taxContribution: 90000,
        sustainableGrowth: 1.1
      }
    },
    details: [
      'Capital inicial para emprendimiento',
      'Fondo de operación para los primeros meses',
      'Inversión en equipo y mobiliario',
      'Presupuesto para marketing y publicidad'
    ]
  },
  {
    id: 'franchise',
    name: 'Adquirir una Franquicia',
    description: 'Invierte en un modelo de negocio probado',
    category: 'business',
    icon: Building2,
    difficulty: 'hard',
    timeline: 'medium',
    monthlyGoal: 90000,
    timeframe: 10,
    certificatesNeeded: 9,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 22.5,
        waterSaved: 56250,
        landPreserved: 9000
      },
      socialImpact: {
        jobsCreated: 4.5,
        familiesSupported: 9,
        communityProjects: 1.35
      },
      economicImpact: {
        localEconomyBoost: 0.9,
        taxContribution: 135000,
        sustainableGrowth: 1.7
      }
    },
    details: [
      'Capital para adquirir una franquicia establecida',
      'Fondo para canon de entrada y regalías iniciales',
      'Inversión en local y adecuaciones',
      'Capital de trabajo para los primeros 12 meses'
    ]
  },
  {
    id: 'world-travel',
    name: 'Vuelta al Mundo',
    description: 'Viaja por el mundo sin preocupaciones financieras',
    category: 'travel',
    icon: Plane,
    difficulty: 'easy',
    timeline: 'short',
    monthlyGoal: 30000,
    timeframe: 7,
    certificatesNeeded: 3,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 7.5,
        waterSaved: 18750,
        landPreserved: 3000
      },
      socialImpact: {
        jobsCreated: 1.5,
        familiesSupported: 3,
        communityProjects: 0.45
      },
      economicImpact: {
        localEconomyBoost: 0.3,
        taxContribution: 45000,
        sustainableGrowth: 0.6
      }
    },
    details: [
      'Fondo para viajes internacionales',
      'Experiencias de lujo y aventura',
      'Flexibilidad para aprovechar oportunidades',
      'Ingresos en dólares para gastos internacionales'
    ]
  },
  {
    id: 'family-legacy',
    name: 'Legado Familiar',
    description: 'Crea un patrimonio duradero para tu familia',
    category: 'legacy',
    icon: Heart,
    difficulty: 'very-hard',
    timeline: 'very-long',
    monthlyGoal: 150000,
    timeframe: 25,
    certificatesNeeded: 15,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 37.5,
        waterSaved: 93750,
        landPreserved: 15000
      },
      socialImpact: {
        jobsCreated: 7.5,
        familiesSupported: 15,
        communityProjects: 2.25
      },
      economicImpact: {
        localEconomyBoost: 1.5,
        taxContribution: 225000,
        sustainableGrowth: 2.8
      }
    },
    details: [
      'Patrimonio significativo para futuras generaciones',
      'Estructura de protección patrimonial',
      'Ingresos pasivos sostenibles a largo plazo',
      'Impacto ambiental y social positivo como legado'
    ]
  },
  {
    id: 'children-fund',
    name: 'Fondo para Hijos',
    description: 'Asegura el futuro de tus hijos desde pequeños',
    category: 'family',
    icon: Baby,
    difficulty: 'medium',
    timeline: 'long',
    monthlyGoal: 70000,
    timeframe: 18,
    certificatesNeeded: 7,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 17.5,
        waterSaved: 43750,
        landPreserved: 7000
      },
      socialImpact: {
        jobsCreated: 3.5,
        familiesSupported: 7,
        communityProjects: 1.05
      },
      economicImpact: {
        localEconomyBoost: 0.7,
        taxContribution: 105000,
        sustainableGrowth: 1.3
      }
    },
    details: [
      'Educación desde preescolar hasta universidad',
      'Actividades extracurriculares',
      'Fondo de emergencias médicas',
      'Patrimonio inicial para su vida adulta'
    ]
  },
  {
    id: 'health-fund',
    name: 'Fondo de Salud',
    description: 'Fondo para cuidado integral de la salud',
    category: 'health',
    icon: Stethoscope,
    difficulty: 'medium',
    timeline: 'medium',
    monthlyGoal: 45000,
    timeframe: 12,
    certificatesNeeded: 5,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 12.5,
        waterSaved: 31250,
        landPreserved: 5000
      },
      socialImpact: {
        jobsCreated: 2.5,
        familiesSupported: 5,
        communityProjects: 0.75
      },
      economicImpact: {
        localEconomyBoost: 0.5,
        taxContribution: 75000,
        sustainableGrowth: 0.9
      }
    },
    details: [
      'Cobertura de gastos médicos mayores',
      'Tratamientos especializados',
      'Medicina preventiva',
      'Atención en el extranjero'
    ]
  },
  {
    id: 'sustainable-living',
    name: 'Vida Sostenible',
    description: 'Transforma tu estilo de vida hacia la sostenibilidad',
    category: 'legacy',
    icon: Leaf,
    difficulty: 'easy',
    timeline: 'short',
    monthlyGoal: 25000,
    timeframe: 8,
    certificatesNeeded: 3,
    impactMetrics: {
      environmentalImpact: {
        co2Reduction: 7.5,
        waterSaved: 18750,
        landPreserved: 3000
      },
      socialImpact: {
        jobsCreated: 1.5,
        familiesSupported: 3,
        communityProjects: 0.45
      },
      economicImpact: {
        localEconomyBoost: 0.3,
        taxContribution: 45000,
        sustainableGrowth: 0.6
      }
    },
    details: [
      'Financiamiento para vivienda ecológica',
      'Sistemas de energía renovable',
      'Huertos y producción de alimentos',
      'Reducción de huella de carbono'
    ]
  }
];